import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { kycDocumentsService, KycSmartDocumentDto } from '../../../../../services/kycDocuments.service';
import { investorService, PersonAccountGroup } from '../../../../../services/investor.service';
import { profileService, ServiceProviderDto } from '../../../../../services/profile.service';

/**
 * D2 "My Documents" manager (UX task C) — journey-LESS documents-center only.
 * Header matches the DASHBOARD banner style (user 2026-08-22): blue gradient bar with the
 * same two pill dropdowns — Service Provider (white) × Accounts (gold), both default All,
 * fail-open (a document that can't be positively attributed elsewhere is never hidden).
 * SP attribution is DERIVED (person → their journeys' SPs) — documents carry no SP by design.
 */
const TYPE_LABEL: Record<string, string> = {
  PAN_CARD: 'PAN card',
  PASSPORT: 'Passport',
  AADHAR_CARD: 'Aadhaar',
  OCI_CARD: 'OCI card',
  ADDRESS_PROOF: 'Address proof',
};

export const MyDocumentsSection: React.FC = () => {
  const [docs, setDocs] = useState<KycSmartDocumentDto[]>([]);
  const [personGroups, setPersonGroups] = useState<PersonAccountGroup[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderDto[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | 'ALL'>('ALL');
  const [selectedSpRef, setSelectedSpRef] = useState<string>('ALL');
  const [spMenuOpen, setSpMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewingId, setViewingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [list, groups, sps] = await Promise.all([
          kycDocumentsService.list().catch(() => [] as KycSmartDocumentDto[]),
          investorService.getPersonAccounts().catch(() => [] as PersonAccountGroup[]),
          profileService.getServiceProviders().catch(() => [] as ServiceProviderDto[]),
        ]);
        setDocs(list || []);
        setPersonGroups(groups || []);
        setServiceProviders(sps || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Close both filter menus on outside click (same behaviour as the dashboard banner).
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setSpMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const guidEq = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.toLowerCase() === b.toLowerCase();

  // The login investor's own person = the first group (a null doc.personId belongs to them).
  const selfPersonId = personGroups[0]?.personId ?? null;

  // Journey-stamped rows carry their exact journey — resolve it from the groups (already
  // loaded; zero extra calls). Registry-lag rows fail open to the shared presentation.
  const journeyOf = (doc: KycSmartDocumentDto) => {
    if (!doc.journeyId) return null;
    for (const g of personGroups) {
      const j = (g.journeys || []).find((x) => guidEq(x.journeyId, doc.journeyId));
      if (j) return { journey: j, group: g };
    }
    return null;
  };

  const groupOf = (doc: KycSmartDocumentDto): PersonAccountGroup | null => {
    // Stamped rows: the JOURNEY's person is authoritative (registry = source of truth).
    const viaJourney = journeyOf(doc);
    if (viaJourney) return viaJourney.group;
    const pid = doc.personId ?? selfPersonId;
    return personGroups.find((g) => g.personId === pid) || null;
  };

  const selectedGroup = selectedPersonId === 'ALL'
    ? null
    : personGroups.find((g) => g.personId === selectedPersonId) || null;
  const selectedSp = selectedSpRef === 'ALL'
    ? null
    : serviceProviders.find((sp) => guidEq(sp.spRef, selectedSpRef)) || null;

  const filtered = useMemo(() => docs.filter((doc) => {
    // Only KYC identity docs here — onboarding sets (CKYC/FATCA, upload_type=2) belong to
    // the journey's onboarding page (user decision 2026-08-22).
    if (doc.uploadType === 2) return false;
    const stamped = journeyOf(doc);
    const group = stamped?.group ?? groupOf(doc);
    if (selectedPersonId !== 'ALL') {
      const pid = group?.personId ?? doc.personId ?? selfPersonId;
      if (pid !== null && pid !== selectedPersonId) return false;
    }
    if (selectedSpRef !== 'ALL') {
      if (stamped) {
        // Exact journey SP for stamped rows.
        if (stamped.journey.spRef && !guidEq(stamped.journey.spRef, selectedSpRef)) return false;
      } else if (group) {
        // Shared vault rows serve every SP the person works with — fail-open.
        const serves = (group.journeys || []).some((j) => guidEq(j.spRef, selectedSpRef));
        if (!serves) return false;
      }
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [docs, selectedPersonId, selectedSpRef, personGroups]);

  const productCell = (doc: KycSmartDocumentDto): string => {
    const stamped = journeyOf(doc);
    if (stamped) return stamped.journey.product || 'Journey document';
    return 'Shared — all journeys';
  };

  const spCell = (doc: KycSmartDocumentDto): string => {
    const stamped = journeyOf(doc);
    if (stamped) return stamped.journey.serviceProviderName || '—';
    // Shared vault doc: the SPs it serves (bounded to THIS person's journeys).
    const group = groupOf(doc);
    const names = Array.from(new Set((group?.journeys || [])
      .map((j) => j.serviceProviderName)
      .filter((n): n is string => !!n)));
    return names.length ? names.join(', ') : '—';
  };

  const accountCell = (doc: KycSmartDocumentDto): string => {
    const stamped = journeyOf(doc);
    if (stamped?.group.displayName) return stamped.group.displayName;
    return doc.personName || personGroups[0]?.displayName || 'My account';
  };

  const nameCell = (doc: KycSmartDocumentDto): string => {
    const label = TYPE_LABEL[doc.documentType || ''] || doc.documentType || 'Document';
    return doc.originalFilename ? `${label} — ${doc.originalFilename}` : label;
  };

  const handleView = async (doc: KycSmartDocumentDto) => {
    if (!doc.id) return;
    setViewingId(doc.id);
    try {
      await kycDocumentsService.viewInNewTab(doc.id);
    } catch {
      toast.error('Could not open the document');
    } finally {
      setViewingId(null);
    }
  };

  if (loading) return null;

  return (
    <div className="mb-4">
      {/* Dashboard-style banner: gradient bar + the two pill filter dropdowns */}
      <div
        className="text-white rounded shadow-sm px-4 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
      >
        <h1 className="mb-0 fw-bold text-white" style={{ fontSize: 16 }}>My Documents</h1>

        <div className="d-flex align-items-center gap-2" ref={filtersRef}>
          {/* Service Provider filter (white pill) */}
          <div className="position-relative">
            <button
              type="button"
              onClick={() => { setSpMenuOpen((o) => !o); setAccountMenuOpen(false); }}
              className="btn btn-sm text-white fw-semibold"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', fontSize: 11 }}
            >
              <span style={{ opacity: 0.7 }}>Service Provider:&nbsp;</span>
              {selectedSp?.spName || 'All'}
              <i className="bi bi-chevron-down ms-2" style={{ fontSize: 9 }}></i>
            </button>
            {spMenuOpen && (
              <div className="position-absolute end-0 mt-1 bg-white rounded shadow border overflow-hidden" style={{ width: 240, zIndex: 1050 }}>
                <button
                  type="button"
                  onClick={() => { setSelectedSpRef('ALL'); setSpMenuOpen(false); }}
                  className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedSpRef === 'ALL' ? 'fw-bold' : ''}`}
                  style={{ fontSize: 11, color: selectedSpRef === 'ALL' ? '#b0842b' : '#334155', cursor: 'pointer' }}
                >
                  All service providers
                </button>
                {serviceProviders.filter((sp) => sp.spRef).map((sp) => (
                  <button
                    key={sp.spRef!}
                    type="button"
                    onClick={() => { setSelectedSpRef(sp.spRef!); setSpMenuOpen(false); }}
                    className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${guidEq(sp.spRef, selectedSpRef) ? 'fw-bold' : ''}`}
                    style={{ fontSize: 11, color: guidEq(sp.spRef, selectedSpRef) ? '#b0842b' : '#334155', cursor: 'pointer' }}
                  >
                    {sp.spName || 'Service Provider'}
                  </button>
                ))}
                {serviceProviders.length === 0 && (
                  <div className="px-3 py-2 text-muted" style={{ fontSize: 11 }}>No service providers yet</div>
                )}
              </div>
            )}
          </div>

          {/* Accounts filter (gold pill, person-based) */}
          <div className="position-relative">
            <button
              type="button"
              onClick={() => { setAccountMenuOpen((o) => !o); setSpMenuOpen(false); }}
              className="btn btn-sm text-white fw-semibold"
              style={{ background: '#B0842B', border: '1px solid #B0842B', fontSize: 11 }}
            >
              <span style={{ opacity: 0.7 }}>Accounts:&nbsp;</span>
              {selectedGroup?.displayName || 'All'}
              <i className="bi bi-chevron-down ms-2" style={{ fontSize: 9 }}></i>
            </button>
            {accountMenuOpen && (
              <div className="position-absolute end-0 mt-1 bg-white rounded shadow border overflow-hidden" style={{ width: 240, zIndex: 1050 }}>
                <button
                  type="button"
                  onClick={() => { setSelectedPersonId('ALL'); setAccountMenuOpen(false); }}
                  className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedPersonId === 'ALL' ? 'fw-bold' : ''}`}
                  style={{ fontSize: 11, color: selectedPersonId === 'ALL' ? '#b0842b' : '#334155', cursor: 'pointer' }}
                >
                  All accounts
                </button>
                {personGroups.filter((g) => g.personId != null).map((g) => (
                  <button
                    key={g.personId!}
                    type="button"
                    onClick={() => { setSelectedPersonId(g.personId!); setAccountMenuOpen(false); }}
                    className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedPersonId === g.personId ? 'fw-bold' : ''}`}
                    style={{ fontSize: 11, color: selectedPersonId === g.personId ? '#b0842b' : '#334155', cursor: 'pointer' }}
                  >
                    <span className="d-block">{g.displayName}</span>
                    <span className="d-block text-muted" style={{ fontSize: 9 }}>
                      {(g.journeys || []).length} {(g.journeys || []).length === 1 ? 'account' : 'accounts'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents for the selected filters */}
      <div className="card p-3 mt-2">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Document Name</th>
                <th>Product</th>
                <th>Person Name</th>
                <th>Service Provider</th>
                <th className="text-end">View</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted small py-3">
                    No documents{docs.length > 0 ? ' match the selected filters' : ' uploaded yet'}.
                  </td>
                </tr>
              )}
              {filtered.map((doc) => (
                <tr key={doc.id}>
                  <td className="small">{nameCell(doc)}</td>
                  <td className="small">{productCell(doc)}</td>
                  <td className="small">{accountCell(doc)}</td>
                  <td className="small">{spCell(doc)}</td>
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={viewingId === doc.id}
                      onClick={() => handleView(doc)}
                    >
                      {viewingId === doc.id ? 'Opening…' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyDocumentsSection;
